<template>
  <div class="image-box" v-loading.body="loading">
    <div class="image-wrapper">
      <div class="item-wrapper" v-for="(item, idx) in data" :key="idx">
        <div class="card-badge">
          <span>&ensp;<!-- 占位 --></span>
          <el-tooltip v-if="!!item.excludeMediaIds" placement="top" effect="dark" :content="`已被限制媒体：${item.excludeMediaNames}`">
            <span class="media-forbid el-icon-remove"></span>
          </el-tooltip>

          <el-tag
            v-if="item.aiContent && [IMAGE_TYPES.VIDEO, IMAGE_TYPES.META, IMAGE_TYPES.SHOT].includes(type)"
            class="find-similar"
            type="info"
            @click="$emit('find-similar', item)"
          >
            <span class="el-icon-search"></span>
            找相似
          </el-tag>

          <el-tag v-if="item.similarityScore > 0" type="info" class="similar-score"
            >相似度：{{ (item.similarityScore * 100).toFixed(2) }}%
          </el-tag>
        </div>

        <!-- 视频 -->
        <div class="material-wrapper">
          <template v-if="[IMAGE_TYPES.VIDEO, IMAGE_TYPES.META, IMAGE_TYPES.AI_REMIX, IMAGE_TYPES.SHOT].includes(type)">
            <div @mouseenter="onVideoBoxMouseEnter(item)" @mouseleave="onVideoBoxMouseLeave(item)">
              <img :src="item.addr" class="item-background" />

              <img
                v-popover="{ url: item.field1, type: 'video', height: item.width > item.height ? 320 : 520 }"
                :src="item.addr"
                loading="lazy"
                class="video-poster-item"
                alt=""
              />
              <div class="check-box">
                <el-checkbox
                  @change="selectItem(item)"
                  border
                  v-model="item.check"
                  class="check-item"
                  :class="{ 'checked-item': item.check }"
                >
                </el-checkbox>
              </div>
            </div>
          </template>
          <!-- 音频 -->
          <template v-else-if="type === IMAGE_TYPES.AUDIO">
            <div>
              <div class="item-bg-color"></div>
              <audio-player :fileurl="item.field1" :id="item.id" :coverurl="item.addr" class="video-item"></audio-player>
              <div class="check-box">
                <el-checkbox
                  border
                  v-model="item.check"
                  class="check-item"
                  :class="{ 'checked-item': item.check }"
                  @change="selectItem(item)"
                >
                </el-checkbox>
              </div>
            </div>
          </template>
          <!-- 图片&LOGO -->
          <template v-else>
            <div>
              <img v-if="type === IMAGE_TYPES.GROUP_IMAGE" :src="item.overviewAddr" class="item-background" />
              <img v-else :src="item.addr" class="item-background" />

              <img
                v-if="type === IMAGE_TYPES.GROUP_IMAGE"
                v-popover="{ url: item.overviewAddr, type: 'image', height: item.width > item.height ? 320 : 520 }"
                loading="lazy"
                :src="item.overviewAddr"
                :class="[item.height < item.width ? 'img-item-cross' : 'img-item-vertical']"
              />
              <img v-else :src="item.addr" loading="lazy" :class="[item.height < item.width ? 'img-item-cross' : 'img-item-vertical']" />
              <div class="check-box">
                <el-checkbox
                  @change="selectItem(item)"
                  border
                  v-model="item.check"
                  class="check-item"
                  :class="{ 'checked-item': item.check }"
                >
                </el-checkbox>
              </div>
            </div>
          </template>

          <div class="image-info">
            <el-tag v-if="item.width" type="info">{{ item.width }}&times;{{ item.height }}</el-tag>
            <el-tag type="info">
              <span>{{ (item.size / (1024 * 1024)).toFixed(2) }}M</span>
              <span v-if="item.duration"> / {{ item.duration }}s</span>
            </el-tag>
          </div>
        </div>

        <MaterialTableInfo @select="selectMaterial" :item="item"></MaterialTableInfo>
      </div>
    </div>
  </div>
</template>
<script setup>
import { set } from 'vue'
import AudioPlayer from '@/components/audio-player/audio-player'
import MaterialTableInfo from './material-table-info.vue'
import { IMAGE_TYPES_STR_VAL as IMAGE_TYPES } from '@/views/material/uploading/consts.js'

// eslint-disable-next-line
const props = defineProps({
  data: { type: Array, default: () => [] },
  type: { type: String },
  loading: { type: Boolean }
})
// eslint-disable-next-line
const emits = defineEmits(['find-similar', 'on-check', 'on-select'])

const onVideoBoxMouseEnter = (item) => {
  set(item, 'hoverState', true)
}
const onVideoBoxMouseLeave = (item) => {
  set(item, 'hoverState', false)
}

const selectItem = (item) => {
  emits('on-check', item)
}

const selectMaterial = (id) => {
  emits('on-select', id)
}
</script>
<style lang="scss" scoped>
@mixin info-tag {
  color: #626262;
  line-height: 16px;
  height: 16px;
  background-color: rgb(213, 213, 213, 0.85);
}

.image-box {
  width: 100%;
  margin: 0 auto;
  position: relative;
  display: flex;
  justify-content: center;

  .image-wrapper {
    width: 100%;
    display: grid;
    grid-template-rows: repeat(auto-fill, 234px); /* 八行，每行高度自适应 */
    grid-template-columns: repeat(auto-fit, minmax(250px, 280px));
    gap: 16px; /* 或您需要的任何间距 */
    justify-content: normal;
    align-items: start; /* 确保内容从每行的顶部开始 */
  }

  .c-list-empty {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .item-wrapper {
    min-height: 198px; /* 示例，确保 item 有一定高度 */
    position: relative;
    border: 1px solid #ebebeb;
    border-radius: 4px;
    position: relative;

    &:hover {
      box-shadow: 0 0 10px 8px #ebebeb;

      .card-badge .find-similar {
        display: inline;
      }

      .material-wrapper {
        .check-box {
          .check-item {
            opacity: 1;
          }
        }
      }

      .image-info {
        display: none;
      }
    }

    .image-info {
      width: 100%;
      padding: 0 5px;
      position: absolute;
      left: 0;
      bottom: 5px;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .el-tag {
        @include info-tag;
      }
    }

    .card-badge {
      position: absolute;
      left: 5px;
      top: 5px;
      z-index: 10;
      display: flex;

      .media-forbid {
        color: rgb(237, 77, 19);
        font-size: 18px;
        margin-right: 6px;
      }

      .find-similar,
      .similar-score {
        @include info-tag;
        cursor: pointer;
        display: none;
      }
    }

    .material-wrapper {
      position: relative;
      height: 154px;
      width: 100%;
      border-bottom: 1px solid #ebebeb;
      overflow: hidden;

      .popover-ref {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 32px;
        height: 32px;
        line-height: 32px;
        text-align: center;
        font-size: 32px;
        z-index: 10;
        color: #fff;
        opacity: 0.7;
        cursor: pointer;
      }

      .check-box {
        cursor: pointer;
        width: 30px;
        height: 30px;
        right: 0;
        top: 0;
        position: absolute;

        .check-item {
          position: absolute;
          right: 4px;
          top: 4px;
          margin: 0;
          cursor: pointer;
          opacity: 0;
          -webkit-transition: all 0.4s;
          -moz-transition: all 0.4s;
          -ms-transition: all 0.4s;
          -o-transition: all 0.4s;
          transition: all 0.4s;
        }

        .checked-item {
          opacity: 1;
        }
      }

      .video-item {
        height: 100%;
        width: 100%;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
      }

      .video-poster-item {
        height: 100%;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
      }

      .img-item-cross {
        width: 100%;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
      }

      .img-item-vertical {
        height: 100%;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
      }

      .item-background {
        width: 100%;
        height: 154px;
        filter: blur(10px);
      }

      .item-bg-color {
        width: 100%;
        height: 100%;
        background-color: #3b3b3b;
      }
    }
  }
}
</style>
